import { askUserQuestionPayloadSchema, type AskUserQuestionPayload } from "../types";

/**
 * Parse the JSON `content` of an `ask_user_question` stream event.
 * Returns `undefined` for anything that is not a valid question payload, so a
 * malformed event never renders a broken card.
 */
export const parseAskUserQuestion = (content: unknown): AskUserQuestionPayload | undefined => {
  if (typeof content !== "string" || !content) return undefined;
  try {
    const result = askUserQuestionPayloadSchema.safeParse(JSON.parse(content));
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
};

/** The user's answer to one question: chosen option labels plus typed text. */
export interface AskUserQuestionAnswer {
  selected: string[];
  other: string;
}

export const isAnswered = (answer: AskUserQuestionAnswer | undefined): boolean =>
  !!answer && (answer.selected.length > 0 || answer.other.trim().length > 0);

/**
 * Format the answers as the user's next chat message. Each answer repeats its
 * question, so the agent can match them and the chat history reads on its own.
 */
export const formatAskUserQuestionAnswers = (
  payload: AskUserQuestionPayload,
  answers: AskUserQuestionAnswer[]
): string =>
  payload.questions
    .map((question, index) => {
      const answer = answers[index] ?? { selected: [], other: "" };
      const parts = [...answer.selected, answer.other.trim()].filter(Boolean);
      return `${question.question}\n→ ${parts.join(", ")}`;
    })
    .join("\n\n");
